import Footer from '@/components/Footer';
import Image from 'next/image';
import Link from 'next/link';

export default function About() {
  return (
    <div className="min-h-screen bg-[#F6EFED]">
      {/* Header - Same as Homepage */}
      <header className="absolute top-0 left-0 right-0 z-50">
        <div className="absolute inset-0 bg-transparent" />
        
        <div className="relative max-w-[1920px] mx-auto px-[80px] py-[40px]">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center" style={{ width: '367.62px', height: '94px' }}>
              <Image
                src="/header-transparent-logo.png"
                alt="Female Fitness Finder"
                width={367.62}
                height={94}
                className="object-contain"
                priority
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-[40px]">
              <Link href="/" className="font-['Archivo',sans-serif] text-[18px] text-white font-normal leading-[100%] tracking-[0%]">Home</Link>
              <Link href="/about" className="font-['Archivo',sans-serif] text-[18px] text-white font-normal leading-[100%] tracking-[0%]">About</Link>
              <Link href="/directory" className="font-['Archivo',sans-serif] text-[18px] text-white font-normal leading-[100%] tracking-[0%]">Directory</Link>
              <Link href="/for-women" className="font-['Archivo',sans-serif] text-[18px] text-white font-normal leading-[100%] tracking-[0%]">For Women</Link>
              <Link href="/contact" className="font-['Archivo',sans-serif] text-[18px] text-white font-normal leading-[100%] tracking-[0%]">Contact</Link>
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-[40px]">
              <div className="w-4 h-4">
                <Image
                  src="/search-icon.svg"
                  alt="Search"
                  width={16}
                  height={16}
                  className="object-contain"
                />
              </div>
              <button className="px-[34px] py-[20px] bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[18px] font-medium leading-[100%] tracking-[0%] rounded-[10px] hover:bg-[#4C3E58] transition-colors flex items-center gap-2 backdrop-blur-sm">
                Register
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
              <Link href="/login" className="font-['Archivo',sans-serif] text-[18px] text-white font-normal leading-[100%] tracking-[0%]">Log In</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Background */}
      <section className="relative h-[600px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/about-banner.jpg"
            alt="About Female Fitness Finder"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#4C3E581A]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1920px] mx-auto h-full flex items-center justify-center absolute bottom-0 left-0 right-0 pt-[180px]">
          <div className="max-w-[842px] text-center">
            <h1 className="font-['Gotham_Rounded',sans-serif] text-[64px] font-bold leading-[76px] tracking-[-4%] text-white mb-10" style={{ marginBottom: '10px' }}>
              About FFF
            </h1>
            <p className="font-['Archivo',sans-serif] text-[20px] font-normal leading-[36px] text-white mb-12 tracking-[3%] max-w-3xl mx-auto" style={{ margin: '0 !important' }}>
              Connecting women with safe, verified, women-only fitness spaces.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content - New Layout */}
      <main className="max-w-[1920px] mx-auto px-[80px] py-[80px]">
        {/* Story Section - Image Left, Content Right */}
        <div className="grid lg:grid-cols-2 gap-[80px] items-center mb-[80px]">
          {/* Left - Image */}
          <div className="relative h-[600px] rounded-[24px] overflow-hidden">
            <Image
              src="/about-image.jpg"
              alt="Women working out together"
              fill
              className="object-cover"
            />
          </div>
          
          {/* Right - Content */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-[8px] h-[8px] bg-[#8a7490] rounded-full" />
              <span className="font-['Archivo',sans-serif] text-[20px] font-medium text-[#8a7490]">Our Story</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2b262e] leading-tight mb-6">
              Empowering Women Through Fitness
            </h2>
            <div className="space-y-4 text-[#6e6672] text-lg leading-relaxed">
              <p>
                We're more than just a platform - we're a movement dedicated to empowering women through fitness. Our mission is to create a supportive, inclusive environment where women can discover the perfect fitness programs, connect with expert trainers, and join communities that celebrate their journey toward health and wellness.
              </p>
              <p>
                Every gym, studio, and trainer on our platform is verified to ensure they provide a genuine women-only or women-focused environment, prioritizing spaces where women feel physically and emotionally safe.
              </p>
            </div>
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div className="grid lg:grid-cols-2 gap-[40px] mb-[80px]">
          {/* Mission Card */}
          <div className="bg-[#8a7490] rounded-[24px] p-[60px] text-white">
            <h3 className="text-3xl font-semibold mb-6">Our Mission</h3>
            <p className="text-lg text-white/90 leading-relaxed">
              To empower women to achieve their fitness goals by providing personalized, accessible, and supportive fitness solutions that adapt to their unique lifestyles and needs. We believe every woman deserves access to fitness spaces where she feels safe, supported, and empowered.
            </p>
          </div>

          {/* Vision Card */}
          <div className="bg-white rounded-[24px] p-[60px] border border-[#e8e1e6]">
            <h3 className="text-3xl font-semibold text-[#2b262e] mb-6">Our Vision</h3>
            <p className="text-lg text-[#6e6672] leading-relaxed">
              To become the leading platform connecting women with fitness resources, creating a global community where every woman feels supported in her health and wellness journey. From yoga to strength training, we envision a world where every woman can find her perfect fitness space.
            </p>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-white rounded-[24px] p-[80px] border border-[#e8e1e6]">
          <div className="flex items-center justify-center gap-2 mb-12">
            <div className="w-[8px] h-[8px] bg-[#8a7490] rounded-full" />
            <h2 className="text-3xl font-bold text-[#2b262e]">Why Choose Us?</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-[60px]">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-[#8a7490]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-[#2b262e] mb-4">Verified Experts</h3>
              <p className="text-lg text-[#6e6672]">All trainers are certified and experienced in women's fitness</p>
            </div>

            {/* Feature 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-[#8a7490]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-[#2b262e] mb-4">Personalized Approach</h3>
              <p className="text-lg text-[#6e6672]">Programs tailored to your specific goals and body type</p>
            </div>

            {/* Feature 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-[#8a7490]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-[#8a7490]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-[#2b262e] mb-4">Supportive Community</h3>
              <p className="text-lg text-[#6e6672]">Connect with women on similar fitness journeys</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
